import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";

import { api } from "../utils/api";
import { ApiResponse, ApiError } from "../types/api";
import { IgnitionRegime } from "../types/ignitionRegime";

export const useIgnitionRegimes = (projectId: number) => {
  const {
    data: ignitionRegimes,
    isLoading,
    error,
  } = useQuery({
    queryKey: [projectId, "ignitionRegimes"],
    queryFn: () =>
      api.request<IgnitionRegime[]>(
        `/api/projects/${projectId}/ignition-regimes/`
      ),
    refetchOnWindowFocus: false,
  }) as {
    data: IgnitionRegime[] | undefined;
    isLoading: boolean;
    error: ApiError;
  };

  return { ignitionRegimes, isLoading, error };
};

export const useAddIgnitionRegime = (projectId: number) => {
  const queryClient = useQueryClient();

  return useMutation<
    ApiResponse<IgnitionRegime>,
    ApiError,
    { name: string; mode?: "blank" | "grid"; grid_spacing_meters?: number }
  >({
    mutationFn: ({ name, mode, grid_spacing_meters }) => {
      return api.request(`/api/projects/${projectId}/ignition-regimes/`, {
        method: "POST",
        body: { name, mode, grid_spacing_meters },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [projectId, "ignitionRegimes"] });
    },
  });
};

export const useAddIgnitionToRegime = (projectId: number) => {
  const queryClient = useQueryClient();

  return useMutation<
    ApiResponse<void>,
    ApiError,
    { regimeId: number; geometry: any; name?: string }
  >({
    mutationFn: async ({ regimeId, geometry, name }) => {
      const response: ApiResponse<void> = await api.request(
        `/api/projects/${projectId}/ignition-regimes/${regimeId}/ignitions`,
        {
          method: "POST",
          body: { geometry, name },
        }
      );
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [projectId, "ignitionRegimes"] });
    },
  });
};

export const useUpdateIgnitionRegime = (projectId: number) => {
  const queryClient = useQueryClient();

  return useMutation<
    ApiResponse<IgnitionRegime>,
    ApiError,
    { regimeId: number; name: string }
  >({
    mutationFn: async ({ regimeId, name }) => {
      const response: ApiResponse<IgnitionRegime> = await api.request(
        `/api/projects/${projectId}/ignition-regimes/${regimeId}`,
        {
          method: "PUT",
          body: { name },
        }
      );
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [projectId, "ignitionRegimes"] });
    },
  });
};

export const useDeleteIgnitionRegime = (projectId: number) => {
  const queryClient = useQueryClient();

  return useMutation<ApiResponse<void>, ApiError, number>({
    mutationFn: async (regimeId) => {
      const response: ApiResponse<void> = await api.request(
        `/api/projects/${projectId}/ignition-regimes/${regimeId}`,
        {
          method: "DELETE",
        }
      );
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [projectId, "ignitionRegimes"] });
    },
  });
};

export const useDeleteIgnition = (projectId: number, regimeId: number) => {
  const queryClient = useQueryClient();

  return useMutation<ApiResponse<void>, ApiError, number>({
    mutationFn: async (ignitionId) => {
      const response: ApiResponse<void> = await api.request(
        `/api/projects/${projectId}/ignition-regimes/${regimeId}/ignitions/${ignitionId}`,
        {
          method: "DELETE",
        }
      );
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [projectId, "ignitionRegimes"] });
    },
  });
};

export const useAddIgnition = (projectId: number, regimeId: number) => {
  const queryClient = useQueryClient();

  return useMutation<
    ApiResponse<void>,
    ApiError,
    { geometry: any; name?: string }
  >({
    mutationFn: async ({ geometry, name }) => {
      const response: ApiResponse<void> = await api.request(
        `/api/projects/${projectId}/ignition-regimes/${regimeId}/ignitions`,
        {
          method: "POST",
          body: { geometry, name },
        }
      );
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [projectId, "ignitionRegimes"] });
    },
  });
};

/**
 * Bulk create with the regime supplied per call, for flows where the target
 * regime may have been created moments earlier in the same interaction (a
 * hook baked with regimeId would still hold the previous value on that
 * render). Mirrors useAddIgnitionToRegime.
 */
export const useAddIgnitionsBulkToRegime = (projectId: number) => {
  const queryClient = useQueryClient();

  return useMutation<
    ApiResponse<{ regime: IgnitionRegime; created_count: number; errors?: any[] }>,
    ApiError,
    { regimeId: number; ignitions: { geometry: any; name?: string }[] }
  >({
    mutationFn: async ({ regimeId, ignitions }) => {
      const response = await api.request<
        ApiResponse<{ regime: IgnitionRegime; created_count: number; errors?: any[] }>
      >(`/api/projects/${projectId}/ignition-regimes/${regimeId}/ignitions`, {
        method: "POST",
        body: { ignitions },
      });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [projectId, "ignitionRegimes"] });
    },
  });
};

export const useAddIgnitionsBulk = (projectId: number, regimeId: number) => {
  const queryClient = useQueryClient();

  return useMutation<
    ApiResponse<{ regime: IgnitionRegime; created_count: number; errors?: any[] }>,
    ApiError,
    { ignitions: { geometry: any; name?: string }[] }
  >({
    mutationFn: async ({ ignitions }) => {
      const response = await api.request<
        ApiResponse<{ regime: IgnitionRegime; created_count: number; errors?: any[] }>
      >(
        `/api/projects/${projectId}/ignition-regimes/${regimeId}/ignitions`,
        {
          method: "POST",
          body: { ignitions },
        }
      );
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [projectId, "ignitionRegimes"] });
    },
  });
};
